import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth, type Role, type ProfileStatus } from '@/lib/auth'
import { useLocale } from '@/lib/useLocale'
import { FullScreenLoader, CenteredCard } from '@/components/layouts'
import { Button } from '@/components/ui/button'

/** Requires an authenticated, approved user. Otherwise routes to login / status screen. */
export function RequireApproved({ allow }: { allow?: Role[] }) {
  const { session, profile, loading } = useAuth()
  const location = useLocation()
  const locale = useLocale()

  if (loading) return <FullScreenLoader />
  if (!session)
    return <Navigate to={`/${locale}/auth/login`} replace state={{ from: location }} />
  if (!profile) return <ProfileLoadError />

  const status: ProfileStatus = profile.status
  if (status === 'pending') return <Navigate to={`/${locale}/auth/pending`} replace />
  if (status === 'rejected') return <Navigate to={`/${locale}/auth/rejected`} replace />
  if (status !== 'approved') return <Navigate to={`/${locale}/auth/pending`} replace />

  if (allow && !allow.includes(profile.role)) {
    return <Navigate to={`/${locale}/dashboard`} replace />
  }
  return <Outlet />
}

/** Admin-only area. */
export function RequireAdmin() {
  return <RequireApproved allow={['admin', 'superadmin']} />
}

/**
 * Session exists but the profile row is missing (DB trigger hasn't run yet, or
 * the fetch failed). Show a retry card instead of spinning forever.
 */
function ProfileLoadError() {
  const { t } = useTranslation()
  const { refreshProfile, signOut } = useAuth()
  return (
    <CenteredCard>
      <h1 className="mb-2 font-heading text-xl font-semibold">{t('auth.profileErrorTitle')}</h1>
      <p className="text-sm text-muted-foreground">{t('auth.profileErrorDesc')}</p>
      <div className="mt-4 flex gap-2">
        <Button onClick={() => void refreshProfile()}>{t('common.retry')}</Button>
        <Button variant="outline" onClick={() => void signOut()}>
          {t('nav.logout')}
        </Button>
      </div>
    </CenteredCard>
  )
}